import { useState, useMemo, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { DownloadIcon, FunnelIcon, SearchIcon, Trash2Icon, XIcon } from 'lucide-react';
import type { RequestLog } from '@mockd/shared';
import { TIER_LIMITS } from '@mockd/shared';
import { isFormUrlEncoded, getContentTypeFromHeaders, parseFormBody } from '@mockd/shared/utils';
import { RequestItem } from './RequestItem';
import { RequestInspector } from './RequestInspector';
import { parseMatchedRules } from './matchedRules';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { CopyButton } from '../common/CopyButton';
import { Loading, LoadingLabel } from '../common/Cursor';
import type { ConnectionStatus } from '../../hooks/useWebSocket';
import { useRequestReplay, type ReplayState } from '../../hooks/useRequestReplay';
import { LiveIndicator } from '../common/LiveIndicator';
import { READ_ONLY_HINT } from '../common/ReadOnlyBadge';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;
const STATUS_CATEGORIES = [
  { label: '2xx Success', min: 200, max: 299 },
  { label: '3xx Redirect', min: 300, max: 399 },
  { label: '4xx Client Error', min: 400, max: 499 },
  { label: '5xx Server Error', min: 500, max: 599 },
] as const;

function parseBodyForExport(body: string | null, headers: Record<string, string>): unknown {
  if (!body) return null;
  const contentType = getContentTypeFromHeaders(headers);
  if (isFormUrlEncoded(contentType)) {
    return parseFormBody(body);
  }
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

function exportToJson(requests: RequestLog[], filename: string): void {
  const data = requests.map(req => {
    const headers = JSON.parse(req.headers || '{}');
    return {
      id: req.id,
      method: req.method,
      path: req.path,
      headers,
      body: parseBodyForExport(req.body, headers),
      timestamp: req.timestamp,
      responseStatus: req.response_status,
      responseTimeMs: req.response_time_ms,
      matchedRuleId: req.matched_response_rule_id,
      matchedRuleName: req.matched_response_rule_name,
      matchedRuleIds: parseMatchedRules(req).map(r => r.id),
      pathParams: req.path_params ? JSON.parse(req.path_params) : null,
    };
  });

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportToCsv(requests: RequestLog[], filename: string): void {
  const headers = ['ID', 'Timestamp', 'Method', 'Path', 'Status', 'Response Time (ms)', 'Matched Rule', 'Headers', 'Body'];
  const rows = requests.map(req => [
    req.id,
    req.timestamp,
    req.method,
    req.path,
    req.response_status?.toString() || '',
    req.response_time_ms?.toString() || '',
    req.matched_response_rule_name || req.matched_response_rule_id || '',
    req.headers || '',
    req.body || '',
  ]);

  const escapeCsv = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(escapeCsv).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface RequestListHandle {
  selectNext: () => void;
  selectPrev: () => void;
  promptClear: () => void;
}

interface RequestListProps {
  requests: RequestLog[];
  status: ConnectionStatus;
  onClear: () => void;
  endpointUrl?: string;
  mockBaseUrl?: string;
  onInspectorChange?: (isOpen: boolean) => void;
  /** True while the first page of stored history is in flight. */
  historyLoading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  historyError?: string | null;
  /** Omit to hide the history footer entirely. */
  onLoadMore?: () => void;
  onRetryHistory?: () => void;
  /** The viewer's plan retention window. Omit to hide the retention banner. */
  retentionDays?: number;
  /** False for a read-only caller: clearing logs is a write the server would refuse. */
  canClear?: boolean;
  /** Rule id → current name, so matched rules show by name. */
  ruleNames?: ReadonlyMap<string, string>;
}

/**
 * Sits at the bottom of the card, deliberately *outside* the list's scroll
 * column: the "No matching requests" empty state replaces the whole list body,
 * so a control inside it would disappear exactly when a filter matches nothing
 * loaded — the moment the user most needs to page further back.
 */
function HistoryFooter({
  loadedCount, hasMore, loadingMore, historyError, hasActiveFilters, onLoadMore, onRetryHistory,
}: {
  loadedCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  historyError?: string | null;
  hasActiveFilters: boolean;
  onLoadMore: () => void;
  onRetryHistory?: () => void;
}) {
  let main: React.ReactNode = null;

  if (historyError) {
    main = (
      <div className="flex items-center gap-2">
        <span className="text-xs text-error">{historyError}</span>
        <button onClick={onRetryHistory ?? onLoadMore} className="btn btn-xs btn-ghost">
          Retry
        </button>
      </div>
    );
  } else if (hasMore) {
    main = (
      <button onClick={onLoadMore} disabled={loadingMore} className="btn btn-sm btn-ghost">
        {loadingMore ? (
          <LoadingLabel>Loading</LoadingLabel>
        ) : (
          'Load older requests'
        )}
      </button>
    );
  } else if (loadedCount > 0) {
    main = <span className="text-xs text-base-content/70">End of stored history</span>;
  }

  // Nothing to say, and the list's own empty state already speaks.
  if (!main && !hasActiveFilters) return null;

  return (
    <div className="px-4 py-3 border-t border-base-200 flex items-center justify-between gap-3 flex-wrap">
      {main ?? <span />}
      {hasActiveFilters && (
        <span className="text-xs text-base-content/70">
          Filters apply to the {loadedCount} request{loadedCount === 1 ? '' : 's'} loaded so far.
        </span>
      )}
    </div>
  );
}

function MobileInspectorOverlay({ request, mockBaseUrl, onClose, replayState, onReplay, ruleNames }: {
  request: RequestLog;
  mockBaseUrl?: string;
  onClose: () => void;
  replayState?: ReplayState;
  onReplay?: () => void;
  ruleNames?: ReadonlyMap<string, string>;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger slide-up animation after mount
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className={`absolute inset-0 z-10 bg-base-100 flex flex-col lg:hidden transition-transform duration-300 ease-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <RequestInspector
        request={request}
        mockBaseUrl={mockBaseUrl}
        onClose={onClose}
        replayState={replayState}
        onReplay={onReplay}
        ruleNames={ruleNames}
      />
    </div>
  );
}

export const RequestList = forwardRef<RequestListHandle, RequestListProps>(function RequestList({ requests, status, onClear, endpointUrl, mockBaseUrl, onInspectorChange, historyLoading = false, loadingMore = false, hasMore = false, historyError, onLoadMore, onRetryHistory, retentionDays, canClear = true, ruleNames }, ref) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const { replay, getReplayState } = useRequestReplay();
  const hasEverConnectedRef = useRef(false);

  useEffect(() => {
    if (status === 'connected') hasEverConnectedRef.current = true;
  }, [status]);

  const handleReplay = useCallback((request: RequestLog) => {
    if (mockBaseUrl) {
      setSelectedRequestId(request.id);
      replay(request, mockBaseUrl);
    }
  }, [replay, mockBaseUrl]);
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      // Method filter
      if (methodFilter && req.method.toUpperCase() !== methodFilter) {
        return false;
      }

      // Status filter
      if (statusFilter && req.response_status !== null) {
        const category = STATUS_CATEGORIES.find(c => c.label === statusFilter);
        if (category && (req.response_status < category.min || req.response_status > category.max)) {
          return false;
        }
      }

      // Text search filter (searches in path, headers, and body)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const pathMatch = req.path.toLowerCase().includes(query);
        const headersMatch = req.headers?.toLowerCase().includes(query);
        const bodyMatch = req.body?.toLowerCase().includes(query);
        if (!pathMatch && !headersMatch && !bodyMatch) {
          return false;
        }
      }

      // Date range filter
      if (startDate) {
        const reqDate = new Date(req.timestamp);
        const filterStart = new Date(startDate);
        if (reqDate < filterStart) {
          return false;
        }
      }

      if (endDate) {
        const reqDate = new Date(req.timestamp);
        const filterEnd = new Date(endDate);
        if (reqDate > filterEnd) {
          return false;
        }
      }

      return true;
    });
  }, [requests, methodFilter, statusFilter, searchQuery, startDate, endDate]);

  const selectedRequest = useMemo(() => {
    if (!selectedRequestId) return null;
    return filteredRequests.find(r => r.id === selectedRequestId) ?? null;
  }, [selectedRequestId, filteredRequests]);

  // Clear selection when selected request gets filtered out
  useEffect(() => {
    if (selectedRequestId && !selectedRequest) {
      setSelectedRequestId(null);
    }
  }, [selectedRequestId, selectedRequest]);

  // Notify parent when inspector opens/closes
  useEffect(() => {
    onInspectorChange?.(selectedRequest !== null);
  }, [selectedRequest, onInspectorChange]);

  const hasActiveFilters = methodFilter || statusFilter || searchQuery || startDate || endDate;

  const clearFilters = useCallback(() => {
    setMethodFilter('');
    setStatusFilter('');
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
  }, []);

  useImperativeHandle(ref, () => ({
    selectNext() {
      if (filteredRequests.length === 0) return;
      const currentIndex = selectedRequestId
        ? filteredRequests.findIndex(r => r.id === selectedRequestId)
        : -1;
      const nextIndex = currentIndex < filteredRequests.length - 1 ? currentIndex + 1 : 0;
      setSelectedRequestId(filteredRequests[nextIndex].id);
    },
    selectPrev() {
      if (filteredRequests.length === 0) return;
      const currentIndex = selectedRequestId
        ? filteredRequests.findIndex(r => r.id === selectedRequestId)
        : 0;
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredRequests.length - 1;
      setSelectedRequestId(filteredRequests[prevIndex].id);
    },
    promptClear() {
      if (canClear) setShowClearConfirm(true);
    },
  }), [filteredRequests, selectedRequestId, canClear]);

  const handleExportJson = useCallback(() => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportToJson(filteredRequests, `request-logs-${timestamp}.json`);
  }, [filteredRequests]);

  const handleExportCsv = useCallback(() => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportToCsv(filteredRequests, `request-logs-${timestamp}.csv`);
  }, [filteredRequests]);

  const handleClearClick = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = () => {
    setSelectedRequestId(null);
    onClear();
    setShowClearConfirm(false);
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  return (
    <div className="card bg-base-100 relative overflow-hidden">
      {/* Header with title and basic controls */}
      <div className="px-4 py-3 border-b border-base-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2>Request Stream</h2>
            <LiveIndicator status={status} hasEverConnected={hasEverConnectedRef.current} />
            {hasActiveFilters && (
              <span className="badge badge-primary badge-sm">
                {filteredRequests.length}/{requests.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn btn-ghost btn-sm ${showFilters ? 'btn-active' : ''}`}
              title="Toggle filters"
              aria-label="Toggle filters"
            >
              <FunnelIcon className="size-4" />
            </button>
            <div className="dropdown dropdown-end">
              <button
                tabIndex={0}
                disabled={filteredRequests.length === 0}
                className="btn btn-ghost btn-sm"
                title="Export logs"
                aria-label="Export logs"
              >
                <DownloadIcon className="size-4" />
              </button>
              <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 bg-base-100 border border-base-300 rounded-box w-40">
                <li>
                  <button onClick={handleExportJson}>
                    Export JSON
                  </button>
                </li>
                <li>
                  <button onClick={handleExportCsv}>
                    Export CSV
                  </button>
                </li>
              </ul>
            </div>
            <button
              onClick={handleClearClick}
              disabled={!canClear || requests.length === 0}
              className="btn btn-ghost btn-sm"
              title={canClear ? 'Clear all logs' : READ_ONLY_HINT}
              aria-label="Clear all logs"
            >
              <Trash2Icon className="size-4" />
            </button>
          </div>
        </div>

        {/* Search bar - always visible */}
        <div className="mt-3">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-base-content/70" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search in path, headers, or body..."
              aria-label="Search requests"
              className="input input-sm w-full pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
                aria-label="Clear search"
              >
                <XIcon className="size-4" />
              </button>
            )}
          </div>
        </div>

        {/* Advanced filters panel */}
        {showFilters && (
          <div className="mt-3 p-3 bg-base-200 rounded-box space-y-3">
            <div className="flex flex-wrap gap-3">
              <div className="fieldset">
                <label className="fieldset-legend">Method</label>
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  className="select select-sm"
                >
                  <option value="">All Methods</option>
                  {HTTP_METHODS.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="fieldset">
                <label className="fieldset-legend">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="select select-sm"
                >
                  <option value="">All Status</option>
                  {STATUS_CATEGORIES.map(cat => (
                    <option key={cat.label} value={cat.label}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="fieldset">
                <label className="fieldset-legend">From</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input input-sm"
                />
              </div>
              <div className="fieldset">
                <label className="fieldset-legend">To</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input input-sm"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="btn btn-ghost btn-xs"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/*
        Retention window, from the project's effective tier — on a team project
        that is the owner's plan, not the viewer's (#260), so "this project's plan".
      */}
      {retentionDays !== undefined && (
        <div className="px-4 py-2 border-b border-base-200 bg-base-200/40 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-base-content/70">
            Showing the last {retentionDays} {retentionDays === 1 ? 'day' : 'days'} of requests on this project's plan.
          </span>
          {retentionDays < TIER_LIMITS.business.logRetentionDays && (
            <Link to="/pricing" className="text-xs link link-primary">
              Upgrade for longer retention
            </Link>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={showClearConfirm}
        title="Clear Request Logs"
        message={`Are you sure you want to clear all ${requests.length} request log${requests.length === 1 ? '' : 's'}? This action cannot be undone.`}
        confirmText="Clear All"
        cancelText="Cancel"
        variant="warning"
        onConfirm={handleConfirmClear}
        onCancel={handleCancelClear}
      />

      {/* Split pane */}
      <div className="flex flex-col lg:flex-row">
        {/* Left: request list */}
        <div className={`max-h-[600px] overflow-y-auto ${selectedRequest ? 'lg:w-[45%] lg:border-r border-base-300' : 'w-full'}`}>
          {filteredRequests.length === 0 && (historyLoading || (status === 'connecting' && !hasEverConnectedRef.current)) ? (
            <Loading
              label={historyLoading ? 'Loading requests…' : 'Connecting to request stream…'}
              className="px-4 py-12"
            />
          ) : filteredRequests.length === 0 ? (
            <div className="px-4 py-12 text-center text-base-content/70">
              {requests.length === 0 ? (
                <>
                  <p className="mb-2">No requests yet</p>
                  {endpointUrl ? (
                    <div className="max-w-md mx-auto">
                      <p className="text-sm mb-3">Try sending your first request:</p>
                      <div className="bg-base-200 border border-base-300 rounded-box p-3 text-left">
                        <code className="text-xs font-mono text-base-content block whitespace-pre-wrap break-all">
                          {`curl ${endpointUrl}`}
                        </code>
                      </div>
                      <div className="mt-2">
                        <CopyButton text={`curl ${endpointUrl}`} label="Copy command" />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm">
                      Send a request to your endpoint to see it appear here
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="mb-2">No matching requests</p>
                  <p className="text-sm">
                    Try adjusting your filters ({requests.length} request{requests.length === 1 ? '' : 's'} hidden)
                  </p>
                  <button
                    onClick={clearFilters}
                    className="btn btn-sm btn-ghost mt-2"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
          ) : (
            filteredRequests.map(request => (
              <RequestItem
                key={request.id}
                request={request}
                mockBaseUrl={mockBaseUrl}
                replayState={getReplayState(request.id)}
                onReplay={handleReplay}
                isSelected={request.id === selectedRequestId}
                onSelect={() => setSelectedRequestId(request.id === selectedRequestId ? null : request.id)}
                ruleNames={ruleNames}
              />
            ))
          )}
        </div>

        {/* Right: inspector (desktop inline) */}
        {selectedRequest && (
          <>
            <div className="hidden lg:flex lg:w-[55%] max-h-[600px] overflow-hidden flex-col">
              <RequestInspector
                request={selectedRequest}
                mockBaseUrl={mockBaseUrl}
                onClose={() => setSelectedRequestId(null)}
                replayState={getReplayState(selectedRequest.id)}
                onReplay={() => handleReplay(selectedRequest)}
                ruleNames={ruleNames}
              />
            </div>
            {/* Mobile: full-card overlay */}
            <MobileInspectorOverlay
              request={selectedRequest}
              mockBaseUrl={mockBaseUrl}
              onClose={() => setSelectedRequestId(null)}
              replayState={getReplayState(selectedRequest.id)}
              onReplay={() => handleReplay(selectedRequest)}
              ruleNames={ruleNames}
            />
          </>
        )}
      </div>

      {onLoadMore && (
        <HistoryFooter
          loadedCount={requests.length}
          hasMore={hasMore}
          loadingMore={loadingMore}
          historyError={historyError}
          hasActiveFilters={Boolean(hasActiveFilters)}
          onLoadMore={onLoadMore}
          onRetryHistory={onRetryHistory}
        />
      )}
    </div>
  );
});
